import React, { useState } from "react";
import { SoundToggle } from "@/components/SoundToggle";
import { playFanfare } from "@/lib/feedbackSounds";
import { Layers, RotateCcw, Check, Repeat } from "lucide-react";
import { NoteGlyph, RestGlyph, StaveLines, NOTE_REST_PAIRS, NOTE_KIND } from "@/components/NotationDiagrams";

const deck = () => NOTE_REST_PAIRS.flatMap(([d, note, rest, len, tip]) => [
  { id: `n${d}`, d, rest: false, name: note, len, tip: "" },
  { id: `r${d}`, d, rest: true, name: rest, len, tip },
]).sort(() => Math.random() - 0.5);

const Face = ({ c }) => (
  <svg viewBox="0 45 200 150" className="mx-auto w-full max-w-[220px] text-foreground" role="img" aria-label="Flash card">
    <StaveLines x1={0} x2={200} />
    {c.rest ? <RestGlyph x={100} d={c.d} /> : <NoteGlyph x={100} p="A4" kind={NOTE_KIND[c.d]} dotted={c.d === 3} />}
  </svg>
);

// On-screen flash cards: see a note or rest, flip to check its name and length.
export const RestFlashCards = ({ testId = "flash-cards" }) => {
  const [cards, setCards] = useState(deck);
  const [flipped, setFlipped] = useState(false);
  const [knew, setKnew] = useState(0);
  const [seen, setSeen] = useState(new Set());
  const total = NOTE_REST_PAIRS.length * 2;
  const c = cards[0];
  const answer = (ok) => {
    const first = !seen.has(c.id);
    setSeen(new Set(seen).add(c.id));
    if (ok && first) setKnew(knew + 1);
    if (ok && cards.length === 1 && knew + (first ? 1 : 0) === total) playFanfare(0.1, "Notes & rests flash cards");
    setCards(ok ? cards.slice(1) : [...cards.slice(1), c]);
    setFlipped(false);
  };
  const restart = () => { setCards(deck()); setKnew(0); setSeen(new Set()); setFlipped(false); };

  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Layers size={14} /> Flash cards</p>
        <SoundToggle testId={`${testId}-sound-toggle`} className="ml-auto" />
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-left`}>{cards.length} card{cards.length === 1 ? "" : "s"} left</p>
      </div>
      {!c ? (
        <div className="mt-4 text-center">
          <p className="text-base font-semibold" data-testid={`${testId}-result`}>All done! You knew {knew} of {total} first time.</p>
          <button type="button" onClick={restart} data-testid={`${testId}-restart`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground"><RotateCcw size={14} /> Shuffle and go again</button>
        </div>
      ) : (
        <>
          <button type="button" onClick={() => setFlipped(!flipped)} data-testid={`${testId}-card`}
            className="mx-auto mt-3 block w-full max-w-sm rounded-2xl border-2 border-primary/40 bg-background/60 p-3 transition-transform duration-200 hover:scale-[1.01]">
            {flipped ? (
              <div className="py-6 animate-in fade-in" data-testid={`${testId}-back`}>
                <p className="font-serif text-2xl">{c.name}</p>
                <p className="mt-1 text-lg font-bold text-primary">{c.len}{c.rest ? " of silence" : ""}</p>
                {c.tip && <p className="mt-2 text-xs text-muted-foreground">{c.tip}</p>}
              </div>
            ) : (
              <div data-testid={`${testId}-front`}><Face c={c} /><p className="text-xs font-semibold text-muted-foreground">Is it a note or a rest? What's it called and how long does it last? Tap to flip.</p></div>
            )}
          </button>
          {flipped && (
            <div className="mt-3 flex justify-center gap-2">
              <button type="button" onClick={() => answer(true)} data-testid={`${testId}-knew`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground"><Check size={13} /> I knew it</button>
              <button type="button" onClick={() => answer(false)} data-testid={`${testId}-again`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:border-primary"><Repeat size={13} /> Not yet, show it again</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
